import { useEffect, useState } from "react";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Button, Col, Input, Row } from "antd";
import ConfirmModal from "../../../global/components/ConfirmModal";
import useDelete from "../../../global/hooks/useDelete";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath } from "react-icons/hi2";
import useGet from "../../../global/hooks/useGet";
import { PrepurchasedLicenseColumns } from "../Components/Columns";
import { getGiftCardRowId } from "../utils/rowId";
import { PREPURCHASED_LICENSE_LIST_SEARCH_KEY } from "../utils/listSearchStorage";

const initialFilterState = {
  GiftCardName: "",
};

const GIFT_CARDS_BASE = `${process.env.REACT_APP_API_URL}/api/giftcards`;

export const PrepurchasedLicense = () => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [listData, setListData] = useState([]);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "GiftCardNo",
    order: "desc",
  });
  const logData = JSON.parse(sessionStorage.getItem("userData"));
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {};
  const initialPagination = {
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  };
  const [pagination, setPagination] = useState(initialPagination);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filter, setFilter] = useState(initialFilterState);
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    sessionStorage.setItem(
      PREPURCHASED_LICENSE_LIST_SEARCH_KEY,
      location.search || "",
    );
  }, [location.search]);

  const query = new URLSearchParams(params);
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  const updatedQuery = query.toString();
  const url = updatedQuery
    ? `${GIFT_CARDS_BASE}/?${updatedQuery}`
    : `${GIFT_CARDS_BASE}/`;
  const { data, loading, refetch } = useGet(url);

  let deleteUrl;
  selectedRowKeys.length > 1
    ? (deleteUrl = `${GIFT_CARDS_BASE}/bulk-delete/`)
    : (deleteUrl = `${GIFT_CARDS_BASE}/${selectedRowKeys}/`);

  const {
    data: deletedData,
    loading: deletedDataLoading,
    error: deletedDataError,
    deleteData,
    deleteMultiple,
  } = useDelete(deleteUrl);

  useEffect(() => {
    const matchedFilter = {};
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined) {
        matchedFilter[key] = modParams[key];
      }
    }

    setFilter((prev) => ({
      ...prev,
      ...matchedFilter,
    }));
  }, []);

  const paginationConfig = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: data?.count,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
      });

      const updatedParams = {
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page: page,
      };
      setParams(updatedParams);
    },
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
    getCheckboxProps: (record) => ({
      disabled: record?.Paid === "Yes",
    }),
  };

  const onRow = (record) => {
    return {
      onClick: () => {
        const rid = getGiftCardRowId(record);
        if (rid != null) navigate(`/prepurchased-license/info/${rid}`);
      },
    };
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""]/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFilter = () => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        filterDict[key] = filter[key];
      }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";

    if (isEmpty) {
      handleReset();
    } else {
      const next = {
        limit: 10,
        sort_by: "GiftCardNo",
        order: "desc",
        page: 1,
        ...filterDict,
      };
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams(next);
      refetch();
    }
  };

  const handleReset = () => {
    setFilter({ ...initialFilterState });
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    const next = {
      limit: 10,
      sort_by: "GiftCardNo",
      order: "desc",
    };
    setParams(next);
    refetch();
  };

  const onDeleteConfirm = async () => {
    try {
      if (selectedRowKeys.length === 1) {
        await deleteData();
      } else {
        await deleteMultiple({ ids: selectedRowKeys });
      }
    } catch (err) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");

      refetch();
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data?.data?.map((el) => ({
        ...el,
        key: getGiftCardRowId(el),
      }));
      setListData(newData);
    }
  }, [data, loading]);

  const breadcrumbItems = [
    {
      label: "Prepurchased License",
      href: "/prepurchased-license",
    },
  ];

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems} />
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        <div className="mb-4">
          <div className="mt-2">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                <Input
                  className="input_text_3 mr10 h-full"
                  placeholder="Name"
                  value={filter.GiftCardName}
                  onChange={handleFilterChange}
                  name="GiftCardName"
                />
              </Col>
            </Row>
          </div>

          <div className="w-full overflow-x-auto">
            <div className="flex justify-between my-4 flex-nowrap min-w-[610px]">
              <div className="flex items-center">
                <Button
                  type="primary"
                  className="mr-2 w-[104px] h-[30px] font-medium"
                  onClick={handleFilter}
                >
                  <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
                  Apply
                </Button>
                <Button className="reset-button" onClick={handleReset}>
                  <HiArrowPath className="!text-[14px] font-medium" />
                  Reset
                </Button>
                <Button
                  type="primary"
                  className="mr-2 -ml-2 w-[104px] h-[30px] font-medium"
                  onClick={() => navigate("/prepurchased-license/add")}
                >
                  <PlusOutlined />
                  Add
                </Button>
                <Button
                  type="primary"
                  disabled={selectedRowKeys?.length === 0 ? true : false}
                  className="mr-2 w-[104px] h-[30px] font-medium"
                  onClick={() => {
                    setShowModal(true);
                  }}
                >
                  <DeleteOutlined />
                  Delete
                </Button>
              </div>
            </div>
          </div>
          <ConfirmModal
            showModal={showModal}
            setShowModal={setShowModal}
            onDeleteConfirm={onDeleteConfirm}
            setSelectedRowKeys={setSelectedRowKeys}
          />
          <GlobalTable
            columns={PrepurchasedLicenseColumns(
              hoveredRow,
              setShowModal,
              setSelectedRowKeys,
              setParams,
              params,
              logData?.role_value === 4 ||
                logData?.role_value === 5 ||
                logData?.role_value === 6,
            )}
            dataSource={listData}
            rowSelection={rowSelection}
            pagination={paginationConfig}
            onRow={(record, rowIndex) => {
              return {
                onMouseEnter: () => setHoveredRow(rowIndex),
                onMouseLeave: () => setHoveredRow(null),
                ...onRow?.(record, rowIndex),
              };
            }}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};
